/**
 * Custodian Status Page (Sprint C14 Lane B WP B-5)
 *
 * Connection state per ASSET CUSTODIAN (e.g. Coinbase Prime), i.e. every
 * integration whose category is CUSTODY. Fi-Tek GWES is a TRANSFER_AGENT, not
 * a custodian, and is deliberately excluded (GH #1959). Backed by the existing
 * /api/admin/integrations endpoint filtered to category=CUSTODY. The
 * institutional platform-admin-api maps each adapter to a PlatformIntegration
 * row and surfaces enabled / status / lastSyncAt.
 *
 * What this page renders:
 *   - One row per CUSTODY integration discovered via GET /api/admin/integrations.
 *   - Status badge (Connected / Configured / Error / Not configured).
 *   - Last-sync timestamp (relative + absolute).
 *   - Vendor + version from the integration config.
 *   - A "Test" action per row, wired to POST /integrations/{id}/test, whose
 *     result (pass, fail with the reason, or a failed request) stays on the row.
 *
 * A pending-pledge count per custodian is not surfaced by any custody adapter
 * route, so the page carries no column for it rather than a column of "n/a".
 *
 * The rows come from GET /integrations/third-party (platform-admin.openapi.yaml),
 * which takes no tenant: status, enablement and last sync are Aaim's
 * platform-wide readings for each custodian. Operator ruling 2026-09-27
 * (custodian-enablement-scope): the page states that scope in one line, worded
 * as on Custodian Connections; a per-institution enablement is a contract GAP
 * (GH #2958).
 *
 * Route: /monitoring/custodian-status
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, RefreshCw } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Skeleton,
  formatDateTime,
  type DataTableColumn,
} from '@aaimplatform/ui';
import {
  useThirdPartyIntegrations,
  useRefreshThirdParty,
  type IntegrationTestResult,
} from '../hooks/useIntegrations';
import type { ThirdPartyIntegration } from '../api/services/integrations';
import { requestChangeHref } from '../utils/request-change';
import { labelFor } from './monitoring/display-format';

// Classify custodians STRICTLY by integration category. The prior
// name-pattern fallback (['coinbase','fitek','fi-tek','fi tek']) force-matched
// Fi-Tek GWES -- categorized TRANSFER_AGENT in the seed (Mission Trust's core
// wealth platform / ESB, NOT an asset custodian) -- into this custodian view.
// That was a repeat product-definition error (GH #1959). The integration
// category is the single source of truth: a genuinely miscategorized custodian
// is fixed at the seed/config layer, never re-derived from its display name.
function isCustodian(integration: ThirdPartyIntegration): boolean {
  return integration.category === 'CUSTODY';
}

/** The scope of the status shown, worded identically on Custodian Connections. */
const PLATFORM_WIDE_STATUS_NOTE =
  'The status shown is Aaim’s platform-wide integration status for each custodian and applies to every institution; custodians have no per-institution enablement.';

function formatRelative(iso: string | undefined | null): string {
  if (!iso) return 'Never';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return 'Never';
  const diffSeconds = Math.floor((Date.now() - ts) / 1000);
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  return `${Math.floor(diffSeconds / 86400)}d ago`;
}

/**
 * The sync time as an absolute instant with its year and zone, or null when
 * there is none to print. A 'Never' sync has no absolute time, so the cell
 * prints no second line rather than a dash standing in for one. The kit
 * formatter carries the zone; the local one printed 'Sep 20, 12:00 PM' with
 * neither year nor zone (UI run r3, fi-cons-3).
 */
function formatAbsolute(iso: string | undefined | null): string | null {
  if (!iso) return null;
  if (Number.isNaN(new Date(iso).getTime())) return null;
  return formatDateTime(iso, { seconds: false });
}

/**
 * The kit Badge prints the label as written. StatusBadge upper-cased it, so a
 * custodian read 'NOT CONFIGURED' here and 'Disabled' in sentence case beside
 * it (UI run r3, fi-cons-8).
 */
const STATUS_BADGE: Record<
  ThirdPartyIntegration['status'],
  { variant: 'success' | 'warning' | 'error' | 'neutral'; label: string }
> = {
  CONNECTED: { variant: 'success', label: 'Connected' },
  // Amber: configured but not yet connected. Red is reserved for ERROR
  // (F-AP-CUSTODIAN-COPY-001).
  CONFIGURED: { variant: 'warning', label: 'Configured' },
  ERROR: { variant: 'error', label: 'Error' },
  NOT_CONFIGURED: { variant: 'neutral', label: 'Not configured' },
};

/**
 * Why a row's Test action is unavailable, or null when it can run. A test of
 * a disabled or unconfigured integration answers nothing an operator can act on.
 */
function testBlockedReason(integration: ThirdPartyIntegration): string | null {
  if (integration.status === 'NOT_CONFIGURED') return 'This custodian is not configured, so its connection cannot be tested.';
  if (!integration.enabled) return 'This custodian is disabled, so its connection cannot be tested.';
  return null;
}

/** The last connection test run from this page, per integration. */
interface TestOutcomeState {
  ok: boolean;
  text: string;
  at: number;
}

function outcomeFromResult(result: IntegrationTestResult): TestOutcomeState {
  if (result.success) {
    return { ok: true, text: `Connection OK, ${result.latencyMs} ms`, at: Date.now() };
  }
  return {
    ok: false,
    text: `Connection failed: ${result.error ?? 'the custodian returned no reason'}`,
    at: Date.now(),
  };
}

function testTime(at: number): string {
  return new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function CustodianStatusPage() {
  // Connectivity + health view for asset custodians (category=CUSTODY, e.g.
  // Coinbase Prime; Fi-Tek GWES is a transfer agent and is excluded). This is
  // the Collateral Monitoring surface: it ALWAYS renders the connection-state
  // grid (or the "no custodians configured" empty state). The tenant-scoped
  // configuration view lives separately under FI Configuration -> Custodian
  // Connections. The prior default-OFF redirect to that config page was removed
  // 2026-06-20 (nav-tree audit follow-on): it made the config page appear under
  // the Collateral Monitoring menu (the same surface in two nav groups). Showing
  // vendor rows that may not apply to a given FI is the tracked
  // F-AP-CUSTODIAN-HARDCODED-001 follow-on (DB-backed tenant-scoped surface).
  const { data, isLoading, isFetching, error, refetch } = useThirdPartyIntegrations();
  const testMutation = useRefreshThirdParty();
  const [testingId, setTestingId] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, TestOutcomeState>>({});

  const custodians = useMemo(() => {
    return (data ?? []).filter(isCustodian);
  }, [data]);

  const handleTest = (id: string) => {
    setTestingId(id);
    testMutation.mutate(id, {
      onSuccess: (result) => {
        setOutcomes((prev) => ({ ...prev, [id]: outcomeFromResult(result) }));
      },
      onError: (err) => {
        setOutcomes((prev) => ({
          ...prev,
          [id]: { ok: false, text: `Test request failed: ${err.message}`, at: Date.now() },
        }));
      },
      onSettled: () => setTestingId(null),
    });
  };

  const columns: DataTableColumn<ThirdPartyIntegration>[] = [
    {
      header: 'Custodian',
      accessor: 'name',
      align: 'left',
      cell: (_value, row) => (
        <div>
          <div className="text-sm font-sans font-semibold text-primary">{row.name}</div>
          {row.description && (
            <div className="text-xs font-sans text-tertiary mt-0.5">{row.description}</div>
          )}
        </div>
      ),
    },
    {
      header: 'Vendor',
      accessor: 'vendor',
      align: 'left',
      cell: (_value, row) => (
        <div>
          <div className="text-sm font-sans text-primary">{row.vendor ?? '--'}</div>
          {row.version && (
            <div className="text-xs font-mono text-tertiary mt-0.5">v{row.version}</div>
          )}
        </div>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      align: 'left',
      cell: (_value, row) => {
        const badge = STATUS_BADGE[row.status] ?? { variant: 'neutral' as const, label: labelFor(String(row.status)) };
        return (
          <div>
            {/* The badge label stays on one line in the table's narrow column;
                'Not configured' broke at its space at 390px. */}
            <span className="whitespace-nowrap">
              <Badge variant={badge.variant}>{badge.label}</Badge>
            </span>
            {!row.enabled && (
              <div className="text-xs font-sans text-tertiary mt-1">Disabled</div>
            )}
          </div>
        );
      },
    },
    {
      header: 'Last Sync',
      accessor: 'lastSyncAt',
      align: 'left',
      cell: (_value, row) => {
        const absolute = formatAbsolute(row.lastSyncAt);
        return (
          <div>
            <div className="flex items-center gap-2 text-sm font-sans text-primary">
              <Clock className="w-3.5 h-3.5 text-tertiary" aria-hidden="true" />
              {formatRelative(row.lastSyncAt)}
            </div>
            {absolute !== null && (
              <div className="text-xs font-mono tabular-nums text-tertiary mt-0.5">
                {absolute}
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: 'Last Test',
      accessor: 'id',
      align: 'left',
      cell: (_value, row) => {
        const outcome = outcomes[row.id];
        const blocked = testBlockedReason(row);
        return (
          <div role="status" aria-live="polite" className="text-xs font-sans max-w-xs">
            {outcome ? (
              <>
                <span className={outcome.ok ? 'text-growth' : 'text-error'}>{outcome.text}</span>
                <span className="text-tertiary">{`, ${testTime(outcome.at)}`}</span>
              </>
            ) : (
              <span className="text-tertiary">{blocked ?? 'Not tested from this page'}</span>
            )}
          </div>
        );
      },
    },
    {
      header: 'Actions',
      accessor: 'id',
      align: 'right',
      cell: (_value, row) => {
        const blocked = testBlockedReason(row);
        const testing = testingId === row.id;
        return (
          <Button
            variant="secondary"
            size="small"
            disabled={testing || blocked !== null}
            onClick={() => handleTest(row.id)}
            aria-label={`Test connection to ${row.name}`}
            title={blocked ?? undefined}
          >
            <span className="inline-flex items-center gap-2">
              <RefreshCw className={`w-3.5 h-3.5 ${testing ? 'animate-spin' : ''}`} aria-hidden="true" />
              Test
            </span>
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      {/* Page heading */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); the body opens
            with the descriptive subtitle, no redundant H1. */}
        <div className="max-w-2xl">
          <p className="text-sm font-sans text-tertiary">
            Connection state for asset custodians.
          </p>
          {/* Source: GET /integrations/third-party, which is platform-wide
              (custodian-enablement-scope ruling). */}
          <p className="mt-1 text-sm font-sans text-tertiary leading-normal">
            {PLATFORM_WIDE_STATUS_NOTE}
          </p>
        </div>
        <Button
          variant="secondary"
          size="small"
          onClick={() => {
            void refetch();
          }}
          title="Refresh now"
          aria-busy={isFetching}
        >
          <span className="inline-flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh
          </span>
        </Button>
      </div>

      {error && (
        <div className="mb-6" role={data ? 'status' : 'alert'}>
          <Alert
            type={data ? 'warning' : 'error'}
            title={data ? 'Custodian refresh failed' : 'Custodian connections could not be read'}
          >
            <div className="space-y-2">
              <div className="text-xs">
                {error instanceof Error ? error.message : 'The custodian list request failed.'}
              </div>
              <Button
                variant="tertiary"
                size="small"
                onClick={() => {
                  void refetch();
                }}
              >
                Retry
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {isLoading ? (
        <Card>
          <div className="p-8">
            <Skeleton className="h-8 w-full mb-4" />
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full mb-2" />
            ))}
          </div>
        </Card>
      ) : !data ? null : custodians.length === 0 ? (
        <Card>
          <div className="p-12 text-center">
            <p className="text-sm font-sans text-primary font-medium mb-1">
              No custodian connections are configured for your institution.
            </p>
            <Link
              to={requestChangeHref({
                surface: 'Custodian status',
                item: 'Add a custodian connection',
              })}
              className="text-xs font-sans text-accent hover:underline"
            >
              Ask Aaim support to add one
            </Link>
          </div>
        </Card>
      ) : (
        <DataTable columns={columns} data={custodians} rowKey="id" />
      )}
    </div>
  );
}
